import { toast } from "sonner";

/**
 * Server Action 에러인지 확인합니다.
 * 배포 후 캐시된 클라이언트가 이전 Server Action ID를 호출할 때 발생합니다.
 */
export function isServerActionNotFoundError(error: unknown): boolean {
  if (error instanceof Error) {
    // Next.js Server Action not found 에러 메시지 패턴
    // UnrecognizedActionError 는 message 가 아니라 error.name 으로 식별된다
    // (Next.js client/components/unrecognized-action-error.js: this.name = 'UnrecognizedActionError').
    return (
      error.name === "UnrecognizedActionError" ||
      error.message.includes("not found on server") ||
      error.message.includes("was not found on the server") ||
      error.message.includes("Server Action") ||
      error.message.includes("server action")
    );
  }
  return false;
}

/**
 * Server Action 응답이 예상과 다른 에러인지 확인합니다.
 * 페이지 네비게이션 중 진행 중이던 Server Action 응답이
 * 새 라우트 컨텍스트에서 해석될 때 발생하는 race condition입니다.
 */
export function isUnexpectedResponseError(error: unknown): boolean {
  if (error instanceof Error) {
    return error.message.includes(
      "An unexpected response was received from the server"
    );
  }
  return false;
}

/**
 * 네트워크 에러인지 확인합니다.
 * Safari의 "Load failed", Chrome/Firefox의 "Failed to fetch" 등
 * 일시적인 네트워크 장애 시 발생합니다.
 */
export function isNetworkError(error: unknown): boolean {
  if (error instanceof TypeError) {
    return (
      error.message === "Load failed" ||
      error.message === "Failed to fetch" ||
      error.message.includes("NetworkError") ||
      error.message.includes("Network request failed")
    );
  }
  return false;
}

/**
 * Server Action 에러 발생 시 사용자에게 새로고침을 안내합니다.
 * 네트워크 에러 발생 시 사용자에게 재시도를 안내합니다.
 * @returns true if the error was handled
 */
export function handleServerActionError(error: unknown): boolean {
  if (isServerActionNotFoundError(error)) {
    toast.error(
      "서버와의 연결이 만료되었습니다. 페이지를 새로고침합니다.",
      {
        duration: 3000,
        id: "server-action-error", // 중복 토스트 방지
      }
    );

    // 3초 후 자동 새로고침
    setTimeout(() => {
      window.location.reload();
    }, 3000);

    return true;
  }

  if (isUnexpectedResponseError(error)) {
    // 페이지 이동 중 발생하는 race condition으로, 사용자 조치가 불필요
    return true;
  }

  if (isNetworkError(error)) {
    toast.error(
      "네트워크 연결이 불안정합니다. 잠시 후 다시 시도해주세요.",
      {
        duration: 4000,
        id: "network-error",
      }
    );
    return true;
  }

  return false;
}

/**
 * 네트워크 에러 발생 시 자동 재시도하는 Server Action 래퍼입니다.
 * @param action 실행할 Server Action
 * @param maxRetries 최대 재시도 횟수 (기본 2회)
 */
export async function retryServerAction<T>(
  action: () => Promise<T>,
  maxRetries: number = 2
): Promise<T> {
  let lastError: unknown;
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await action();
    } catch (error) {
      lastError = error;
      if (!isNetworkError(error) || attempt === maxRetries) {
        throw error;
      }
      // 지수 백오프: 1초, 2초
      await new Promise((resolve) =>
        setTimeout(resolve, 1000 * (attempt + 1))
      );
    }
  }
  throw lastError;
}

/**
 * Server Action을 안전하게 실행하고 에러를 처리합니다.
 * @param action 실행할 Server Action
 * @param onError Server Action 에러가 아닌 경우 실행할 에러 핸들러
 */
export async function safeServerAction<T>(
  action: () => Promise<T>,
  onError?: (error: unknown) => void
): Promise<T | null> {
  try {
    return await action();
  } catch (error) {
    if (handleServerActionError(error)) {
      return null;
    }
    if (onError) {
      onError(error);
    }
    throw error;
  }
}
